import { type FC, useCallback } from "react";

import { type ApolloQueryResult } from "@apollo/client/core/types";
import { Button, LineHeightInPx, SearchableCard, Spacing, Spinner } from "@snapchat/snap-design-system";

import { type MyLensesLens } from "../../../gql-generated/types";
import { SearchContainer } from "../../../layout/components";
import { type GetLensesListQuery } from "../../../lenses/api/queries";
import { SearchableListContainer, SearchableListSizeConfig } from "../../styles";
import { Flexbox } from "../Flexbox.styles";
import { VirtualizedList } from "../VirtualizedList";

import { allLensesButton } from "./LensesList.styles";
import { LoadableLensRow } from "./LoadableLensRow";
import { AllLenses } from "./messages.generated";

const allLensesButtonHeight = LineHeightInPx.REGULAR + Spacing.MD * 2;

interface LensSearchList {
    lenses: readonly MyLensesLens[];
    selectedLens: Pick<MyLensesLens, "id"> | null;
    setSelectedLens: (lens: MyLensesLens | null) => void;
    searchText: string;
    setSearchText: (searchText: string) => void;
    hideAllLensesLink?: boolean;
    isFirstFetching?: boolean;
    isReFetching?: boolean;
    hasMore?: boolean;
    loadMore?: () => Promise<ApolloQueryResult<GetLensesListQuery>>;
}

export const LensSearchList: FC<LensSearchList> = ({
    lenses,
    selectedLens,
    setSelectedLens,
    searchText,
    setSearchText,
    hideAllLensesLink,
    isFirstFetching = false,
    isReFetching = false,
    hasMore = false,
    loadMore,
}) => {
    const onSelectAllLenses = useCallback(() => {
        setSelectedLens(null);
    }, [setSelectedLens]);

    if (isFirstFetching) {
        return (
            <SearchableListContainer width={SearchableListSizeConfig.width} height={SearchableListSizeConfig.height}>
                <Flexbox alignItems="center" justifyContent="center" width="100%" height="100%">
                    <Spinner data-testid="lens-search__spinner" />
                </Flexbox>
            </SearchableListContainer>
        );
    }

    return (
        <SearchableListContainer
            width={SearchableListSizeConfig.width}
            height={SearchableListSizeConfig.height + allLensesButtonHeight}
        >
            <SearchableCard
                onSearch={setSearchText}
                searchValue={searchText}
                searchPlaceholder="Search..."
                padBody={false}
                searchTestId="lens-search__search-box"
            >
                {!hideAllLensesLink && (
                    <Button
                        type="link"
                        className={allLensesButton}
                        onClick={onSelectAllLenses}
                        data-testid="lens-search__all-lenses"
                    >
                        <AllLenses />
                    </Button>
                )}
                <SearchContainer
                    loading={isReFetching}
                    height={SearchableListSizeConfig.height}
                    noResults={lenses.length === 0}
                >
                    <VirtualizedList
                        data={{ list: lenses, onSelect: setSelectedLens, selectedId: selectedLens?.id }}
                        renderMore={loadMore}
                        hasMore={hasMore}
                        itemSize={SearchableListSizeConfig.lensHeight}
                        width={SearchableListSizeConfig.width}
                        height={SearchableListSizeConfig.height}
                    >
                        {LoadableLensRow}
                    </VirtualizedList>
                </SearchContainer>
            </SearchableCard>
        </SearchableListContainer>
    );
};
